import {isUndefined} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {TextAreaBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {SurveyStarsInput} from '../../../../libs/shared/ui/src/components/surveyStarsInput';
import {greys} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {Button} from '../common/button';
import {Loader} from '../common/loader';
import {TextAreaCounter} from '../common/textAreaCounter';

/*
 * Props.
 */

export interface SurveyFormProps {
  submitForm(score: number, response?: string, contact?: string): void;
  includeContactInput: boolean;
  routeScore?: number;
  isFormSubmitting?: boolean;
  surveyPrompt: string;
  secondaryColor: string;
  answerFieldPlaceholderText: string;
  buttonText: string;
  primaryButtonText: 'light' | 'dark';
  isPreview: boolean;
}

/*
 * Constants.
 */

const maxResponseCharacters = 255;

/*
 * Style.
 */

const StyledTextAreaDiv = styled.div`
  width: 100%;
  position: relative;
`;

const FeedbackPrompt = styled.div`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
`;

const SytledButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledLoaderDiv = styled.div`
  margin-left: 20px;
`;

const StyledTextAreaBox = styled(TextAreaBox)`
  min-height: 80px;
`;

const StyledLabelSpan = styled.span`
  font-size: ${fontSizes.medium};
  color: ${greys.shade80};
`;

const StyledEmailDiv = styled.div`
  width: 100%;
`;

/*
 * Component.
 */

export const SurveyForm: FC<SurveyFormProps> = ({
  submitForm,
  includeContactInput,
  routeScore,
  isFormSubmitting,
  surveyPrompt,
  secondaryColor,
  answerFieldPlaceholderText,
  buttonText,
  primaryButtonText,
  isPreview,
}) => {
  const [score, setScore] = useState<number | undefined>(routeScore);
  const [response, setResponse] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);

  const isSubmitDisabled = isFormSubmitting || isUndefined(score) || response.length > maxResponseCharacters;

  const onFormSubmit = () => {
    if (isPreview) {
      setScore(undefined);
      setResponse('');
      setEmail('');
      return;
    }

    // Email is not currently required, so an empty email is
    // considered valid also
    const isCurrentEmailValid = !email || isEmail(email);
    if (!isUndefined(score) && isCurrentEmailValid) {
      submitForm(score, response, email);
      return;
    }

    // If we didn't submit the form succesfully, update the email state
    setIsValidEmail(isCurrentEmailValid);
  };

  return (
    <>
      <FeedbackPrompt>{surveyPrompt}</FeedbackPrompt>
      <SurveyStarsInput setScore={setScore} score={score} color={secondaryColor} />
      <StyledTextAreaDiv>
        <StyledTextAreaBox
          key="textResponse"
          value={response}
          placeholder={answerFieldPlaceholderText}
          size={VisualSizesEnum.MEDIUM}
          onChange={(value) => setResponse(value)}
        />
        <TextAreaCounter textLength={response.length} textLengthLimit={maxResponseCharacters} />
      </StyledTextAreaDiv>
      {includeContactInput ? (
        <StyledEmailDiv>
          <TextBox
            placeholder="you@yourcompany.com"
            label={<StyledLabelSpan>Email</StyledLabelSpan>}
            size={VisualSizesEnum.MEDIUM}
            onChange={setEmail}
            hasError={!isValidEmail}
            errorMessage="Please enter a valid email"
          />
        </StyledEmailDiv>
      ) : null}
      <SytledButtonWrapper>
        <Button
          color={primaryButtonText}
          background={secondaryColor}
          onClick={onFormSubmit}
          isDisabled={isSubmitDisabled}
        >
          {buttonText}
        </Button>
        {isFormSubmitting && (
          <StyledLoaderDiv>
            <Loader color={secondaryColor} />
          </StyledLoaderDiv>
        )}
      </SytledButtonWrapper>
    </>
  );
};

/*
 * Helpers.
 */

function isEmail(value: string) {
  return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    value,
  );
}
